import React, { useEffect, useState } from 'react';
import { CalendarClockIcon, Loader2Icon, MailIcon, MessageSquareIcon, PhoneIcon, UsersIcon, VideoIcon } from 'lucide-react';
import { Modal } from '../ui/Modal';
import type { Channel, CommunicationSubject, Outcome } from '../../types/portal';
interface LogCommunicationModalProps {
  subject: CommunicationSubject | null;
  onClose: () => void;
  onLogged: (message: string) => void;
}
const channels: {
  id: Channel;
  label: string;
  icon: typeof PhoneIcon;
}[] = [{
  id: 'call',
  label: 'Call',
  icon: PhoneIcon
}, {
  id: 'text',
  label: 'Text',
  icon: MessageSquareIcon
}, {
  id: 'email',
  label: 'Email',
  icon: MailIcon
}, {
  id: 'in-person',
  label: 'In person',
  icon: UsersIcon
}, {
  id: 'video',
  label: 'Video',
  icon: VideoIcon
}];

/** Outcomes only make sense in the context of how you reached out. */
const outcomesByChannel: Record<Channel, {
  id: Outcome;
  label: string;
}[]> = {
  call: [{
    id: 'connected',
    label: 'Connected — spoke with them'
  }, {
    id: 'left-voicemail',
    label: 'Left a voicemail'
  }, {
    id: 'no-answer',
    label: 'No answer — no voicemail left'
  }, {
    id: 'meeting-set',
    label: 'Connected — meeting scheduled'
  }, {
    id: 'application-started',
    label: 'Connected — application started'
  }, {
    id: 'not-interested',
    label: 'Connected — not interested right now'
  }],
  text: [{
    id: 'replied',
    label: 'They replied'
  }, {
    id: 'sent-no-reply',
    label: 'Sent — no reply yet'
  }, {
    id: 'meeting-set',
    label: 'Replied — meeting scheduled'
  }, {
    id: 'not-interested',
    label: 'Replied — not interested right now'
  }],
  email: [{
    id: 'replied',
    label: 'They replied'
  }, {
    id: 'sent-no-reply',
    label: 'Sent — no reply yet'
  }, {
    id: 'meeting-set',
    label: 'Replied — meeting scheduled'
  }, {
    id: 'application-started',
    label: 'Replied — application started'
  }, {
    id: 'bounced',
    label: 'Bounced / undeliverable'
  }, {
    id: 'not-interested',
    label: 'Replied — not interested right now'
  }],
  'in-person': [{
    id: 'met',
    label: 'Met in person'
  }, {
    id: 'meeting-set',
    label: 'Met — next meeting scheduled'
  }, {
    id: 'application-started',
    label: 'Met — application started'
  }, {
    id: 'no-show',
    label: 'They did not show'
  }, {
    id: 'not-interested',
    label: 'Met — not interested right now'
  }],
  video: [{
    id: 'connected',
    label: 'Attended — spoke with them'
  }, {
    id: 'meeting-set',
    label: 'Attended — follow-up scheduled'
  }, {
    id: 'application-started',
    label: 'Attended — application started'
  }, {
    id: 'no-show',
    label: 'They did not join'
  }, {
    id: 'not-interested',
    label: 'Attended — not interested right now'
  }]
};
const followUps = [{
  id: 'tomorrow',
  label: 'Tomorrow'
}, {
  id: '3-days',
  label: 'In 3 days'
}, {
  id: 'next-week',
  label: 'Next week'
}, {
  id: 'none',
  label: 'No follow-up'
}];
export function LogCommunicationModal({
  subject,
  onClose,
  onLogged
}: LogCommunicationModalProps) {
  const [channel, setChannel] = useState<Channel>('call');
  const [outcome, setOutcome] = useState<Outcome>(outcomesByChannel.call[0].id);
  const [notes, setNotes] = useState('');
  const [followUp, setFollowUp] = useState('tomorrow');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!subject) return;
    setChannel('call');
    setOutcome(outcomesByChannel.call[0].id);
    setNotes('');
    setFollowUp('tomorrow');
    setError(null);
    setSaving(false);
  }, [subject]);
  if (!subject) return null;
  const handleSave = () => {
    if (notes.trim().length < 3) {
      setError('Add a short note so the activity history stays useful.');
      return;
    }
    setError(null);
    setSaving(true);
    window.setTimeout(() => {
      setSaving(false);
      const channelLabel = channels.find(item => item.id === channel)?.label ?? 'Activity';
      const followUpLabel = followUps.find(item => item.id === followUp);
      onLogged(`${channelLabel} logged for ${subject.name}${followUp === 'none' ? '' : ` · follow-up set for ${followUpLabel?.label.toLowerCase()}`}.`);
      onClose();
    }, 800);
  };
  return <Modal open onClose={onClose} title={`Log communication — ${subject.name}`} description={subject.context} footer={<>
          <button type="button" onClick={onClose} className="inline-flex h-11 items-center justify-center rounded-lg border border-navy-200 px-4 text-sm font-semibold text-navy-700 hover:bg-navy-50" data-id="element-206" data-mp="b3kjz655swv4">
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={saving} className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-70" data-id="element-207" data-mp="g2x77rzy05y8">
            {saving ? <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-208" data-mp="1z125s14q1oi3" /> : null}
            {saving ? 'Saving…' : 'Save activity'}
          </button>
        </>} data-id="element-205" data-mp="13s8d1nw5gqj8">
      <div className="space-y-5" data-id="element-209" data-mp="1q2huhl1hqh3w">
        {subject.phone || subject.email ? <p className="rounded-lg bg-navy-50 px-3 py-2 text-xs text-navy-600" data-id="element-210" data-mp="1v005dgz2j6ej">
            {subject.phone ? <span data-id="element-211" data-mp="2ipkqo6ip2fqz">{subject.phone}</span> : null}
            {subject.phone && subject.email ? <span data-id="element-212" data-mp="2mrpg0t240q7t"> · </span> : null}
            {subject.email ? <span data-id="element-213" data-mp="2mqeb2pwxhcf4">{subject.email}</span> : null}
          </p> : null}

        <fieldset data-id="element-214" data-mp="28sprrmiiedc6">
          <legend className="text-sm font-semibold text-navy-800" data-id="element-215" data-mp="28r5147zbfejl">
            How did you reach out?
          </legend>
          <div className="mt-2 flex flex-wrap gap-2" data-id="element-216" data-mp="2dt42xdfeq3ef">
            {channels.map(item => {
            const Icon = item.icon;
            const active = channel === item.id;
            return <button key={item.id} type="button" onClick={() => {
              setChannel(item.id);
              setOutcome(outcomesByChannel[item.id][0].id);
            }} aria-pressed={active} className={`inline-flex h-11 items-center gap-2 rounded-lg border px-3 text-sm font-semibold ${active ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-navy-200 text-navy-700 hover:bg-navy-50'}`} data-id="element-217" data-mp="129rkvih289ws">
                  <Icon className="h-4 w-4" aria-hidden="true" data-id="element-218" data-mp="3mny7fmo4utr5" />
                  {item.label}
                </button>;
          })}
          </div>
        </fieldset>

        <div data-id="element-219" data-mp="3hqy25dz3xv13">
          <label htmlFor="log-outcome" className="block text-sm font-semibold text-navy-800" data-id="element-220" data-mp="1kwl2wyq2unvm">
            Outcome
          </label>
          <select id="log-outcome" value={outcome} onChange={event => setOutcome(event.target.value as Outcome)} className="mt-1.5 h-11 w-full rounded-lg border border-navy-200 bg-white px-3 text-sm text-navy-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200" data-id="element-221" data-mp="1fy1jb4yyho6f">
            {outcomesByChannel[channel].map(item => <option key={item.id} value={item.id} data-id="element-222" data-mp="228033yd3mxya">
                {item.label}
              </option>)}
          </select>
        </div>

        <div data-id="element-223" data-mp="19ggfm29gyxqh">
          <label htmlFor="log-notes" className="block text-sm font-semibold text-navy-800" data-id="element-224" data-mp="19ib3npg1f5ys">
            Notes
          </label>
          <textarea id="log-notes" rows={4} value={notes} onChange={event => setNotes(event.target.value)} placeholder="What was discussed and what happens next?" aria-invalid={Boolean(error)} aria-describedby={error ? 'log-notes-error' : undefined} className={`mt-1.5 w-full rounded-lg border p-3 text-sm text-navy-900 placeholder:text-navy-400 focus:outline-none focus:ring-2 ${error ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-200' : 'border-navy-200 focus:border-brand-500 focus:ring-brand-200'}`} data-id="element-225" data-mp="1ihodij82pwk1" />
          {error ? <p id="log-notes-error" className="mt-1.5 text-xs font-medium text-danger-600" data-id="element-226" data-mp="2j7ebd928vx1">
              {error}
            </p> : null}
        </div>

        <fieldset data-id="element-227" data-mp="ci6fkarhxpa0">
          <legend className="flex items-center gap-1.5 text-sm font-semibold text-navy-800" data-id="element-228" data-mp="cglmkpktyee9">
            <CalendarClockIcon className="h-4 w-4" aria-hidden="true" data-id="element-229" data-mp="7jv3dtdqb2om" />
            Schedule the next follow-up
          </legend>
          <div className="mt-2 flex flex-wrap gap-2" data-id="element-230" data-mp="2e9jgypu99f1b">
            {followUps.map(item => {
            const active = followUp === item.id;
            return <button key={item.id} type="button" onClick={() => setFollowUp(item.id)} aria-pressed={active} className={`inline-flex h-11 items-center rounded-lg border px-3 text-sm font-semibold ${active ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-navy-200 text-navy-700 hover:bg-navy-50'}`} data-id="element-231" data-mp="otgxpfxyowkc">
                  {item.label}
                </button>;
          })}
          </div>
        </fieldset>

        <p className="rounded-lg bg-teal-50 px-3 py-2 text-xs font-medium text-teal-700 ring-1 ring-teal-200" data-id="element-232" data-mp="3safp6b3qfn8u">
          Saving this activity credits {subject.goalCredit} and updates your
          Perfect Day progress.
        </p>
      </div>
    </Modal>;
}